import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ICONS from "../../../../assets/Icons";
import StatusContainer from "../../../../components/_utils/CommonStatus/StatusContainer";
import InfoCard from "../../../../components/_utils/CommonStatus/StatusSubComponents/StatusCardContainer/InfoCard";
import TabsWeapperSection from "../../../../components/_utils/CommonStatus/StatusSubComponents/TabsWrapperSection";
import FormatAmount from "../../../../components/_utils/FormatAmount/FormatAmount";
import { setHideBreadCrumb } from "../../../../redux/features/hideBreadCrumb/hideBreadCrumb";

import { GlobalConfiguration } from "../../../../utils/configurationProvider";

/**
 * TestAuthMatrixOutcomePage - Displays the result of a test authorization matrix evaluation.
 *
 * This component renders the outcome page after a user tests an authorization matrix configuration.
 * It handles three distinct states:
 * - success: Transaction approved (all rule requirements matched)
 * - error: Transaction failed (selected user not in any matrix)
 * - partial: Transaction approved by selected user but further approvals pending
 *
 * The component receives its data via React Router's location state, which includes:
 * - page: The outcome state ('success', 'error', 'partial')
 * - selectedUser: The user who was tested
 * - selectedPreviousUsers: Array of previous approvers in the chain
 * - transaction_auth_matrices: Matrix configuration that was evaluated
 * - predicted_approval: Predicted approval grades for pending states
 *
 * @returns {JSX.Element} Rendered outcome page with status, approver details, and rule information
 */
const TestAuthMatrixOutcomePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const {
    page,
    selectedUser,
    selectedPreviousUsers,
    transaction_auth_matrices,
    predicted_approval,
  } = state || {};

  // Determine status string based on page state for StatusContainer component
  const status =
    page === "success"
      ? "success"
      : page === "error"
        ? "error"
        : page === "partial"
          ? "pending"
          : "";

  const [rulePassedCount, setRulePassedCount] = useState([]);

  /**
   * Helper to construct full name from user object.
   * Safely handles missing first/last name by filtering falsy values.
   */
  const getFullName = (user) =>
    [user?.first_name, user?.last_name].filter(Boolean).join(" ");

  // Button configuration for the status container - dynamic colors based on status
  const buttonRows = [
    [
      {
        btnText: "Test new",
        enabledBgColor: "var(--text-primary)",
        enabledTextColor:
          status === "success"
            ? "var(--positive-bg)"
            : status === "error"
              ? "var(--negative-bg)"
              : "var(--neutral-bg)",
        icon: ICONS?.ADD_BUTTON_WHITE,
        handleFunction: () => {
          navigate("/settings/test_authorization_matrix");
        },
        iconToBeAlign: "left",
        fullWidthRequired: true,
        imgColor:
          status === "success"
            ? "var(--positive-bg)"
            : status === "error"
              ? "var(--negative-bg)"
              : "var(--neutral-bg)",
      },
    ],
    [
      {
        btnText: "Go to dashboard",
        enabledBgColor:
          status === "success"
            ? "var(--positive-bg)"
            : status === "error"
              ? "var(--negative-bg)"
              : "var(--neutral-bg)",
        enabledTextColor: "var(--text-primary)",
        icon: ICONS?.DASHBOARD,
        handleFunction: () => {
          navigate("/settings");
        },
        iconToBeAlign: "left",
        fullWidthRequired: true,
        outLineColor: "var(--text-primary)",
        hoverOption: "dark",
      },
    ],
  ];

  /**
   * Computes dynamic account/grade display data based on page state.
   *
   * For 'success' page: Uses rulePassedCount (populated from transaction_auth_matrices definition)
   * to show each role/grade and the count of approvals required.
   *
   * For 'partial' page: Uses predicted_approval.predicted_approval_grades to show
   * which grades are still pending approval.
   *
   * For 'error' page: Returns empty array (handled by conditional rendering in JSX).
   *
   */
  const dynamicAccounts =
    page === "success"
      ? rulePassedCount &&
        rulePassedCount.length > GlobalConfiguration?.lengthCheckZero
        ? rulePassedCount.map((val) => ({
            leftTitle: val?.role,
            leftSubTitle: "Grade",
            rightText: true,
            rightTitle: val?.count,
            rightSubTitle: "Approval/s",
            rightTitleTextColor: "var(--text-primary)",
            rightTextFontWeight: "normal",
          }))
        : []
      : predicted_approval?.predicted_approval_grades &&
          predicted_approval.predicted_approval_grades.length >
            GlobalConfiguration?.lengthCheckZero
        ? predicted_approval.predicted_approval_grades.map((val, index) => ({
            leftTitle: val,
            leftSubTitle: "Grade",
            showBorder:
              predicted_approval.predicted_approval_grades.length -
                GlobalConfiguration?.lengthCheckOne !==
              index,
          }))
        : [];

  /**
   * Redirect to home if page state is missing (invalid navigation).
   * Runs once on mount since dependency array is empty.
   */
  useEffect(() => {
    if (!state?.page) {
      navigate("/home", { replace: true });
    }
  }, []);

  /**
   * Manages breadcrumb visibility and parses rule definition for success state.
   *
   * On mount: Hides breadcrumb and parses transaction_auth_matrices definition
   * to extract role-count pairs for the 'success' page display.
   *
   * The definition string format: "role1~count1<->role2~count2<->..."
   * Example: "G1~2<->G2~1" means Grade 1 needs 2 approvals, Grade 2 needs 1.
   *
   * Cleanup: Restores breadcrumb visibility on unmount.
   */
  useEffect(() => {
    dispatch(setHideBreadCrumb({ hide: true }));
    if (page === "success" && transaction_auth_matrices) {
      const definition =
        transaction_auth_matrices?.[GlobalConfiguration?.lengthCheckZero]
          ?.definition;

      setRulePassedCount(
        definition.split("<->").map((item) => {
          const [role, count] = item.split("~");
          return {
            role,
            count: Number(count),
          };
        }),
      );
    }
    return () => {
      dispatch(setHideBreadCrumb({ hide: false }));
    };
  }, [dispatch]);

  return (
    <>
      <StatusContainer
        status={status}
        // Dynamic title based on page state
        title={
          page === "error"
            ? "Transaction approval failed."
            : page === "success"
              ? "Transaction approval successful"
              : page === "partial"
                ? "Transaction Approval Pending"
                : ""
        }
        // Dynamic subtitle with context-specific messaging
        subtitle={
          page === "error" ? (
            "Transaction approval failed as the selected user is not present in any of the matrix."
          ) : page === "success" ? (
            <>
              <div className="text-font-sm">
                Transaction has been approved successfully as all the
                requirements of the rule matches.
              </div>
            </>
          ) : page === "partial" ? (
            "Transaction has been approved by the selected user but further approvals are pending."
          ) : (
            ""
          )
        }
        buttonRows={buttonRows}
      >
        {/* ============================================================
           RENDER BRANCH 1: ERROR STATE
           Shows selected user and previous approvers with error styling.
           Only rendered when page === "error"
        ============================================================ */}
        {page === "error" ? (
          <TabsWeapperSection title="Approved by">
            <>
              <InfoCard
                bgColor={
                  page === "error"
                    ? "var(--negative-card)"
                    : "var(--neutral-card)"
                }
                header={true}
                rightHeaderText="Selected user"
                rightHeaderTextSize="var(--font-xs)"
                rightHeaderTextColor="var(--text-secondary)"
                rightHeaderFontWeight="normal"
                leftHeaderText={getFullName(selectedUser)}
                leftHeaderTextColor="var(--text-secondary)"
                leftHeaderTextSize="var(--font-xs)"
                borderColor={
                  page === "error" ? "var(--negative-bg)" : "var(--neutral-bg)"
                }
                rows={[
                  {
                    rightTitle: selectedUser?.grade,
                    rightSubTitle: "Grade",
                    rightText: true,
                    rightTitleTextColor: "var(--text-primary)",
                    leftTitle: selectedUser?.user_id,
                    leftSubTitle: "User ID",
                    leftTitleTextColor: "var(--text-primary)",
                    rightTextFontWeight: "normal",
                  },
                ]}
              />

              {selectedPreviousUsers?.map((val, index) => {
                return (
                  <InfoCard
                    bgColor={
                      page === "error"
                        ? "var(--negative-card)"
                        : "var(--neutral-card)"
                    }
                    header={true}
                    rightHeaderText="Previous approver"
                    rightHeaderFontWeight="normal"
                    rightHeaderTextSize="var(--font-xs)"
                    rightHeaderTextColor="var(--text-secondary)"
                    leftHeaderText={getFullName(val)}
                    leftHeaderTextColor="var(--text-secondary)"
                    leftHeaderTextSize="var(--font-xs)"
                    borderColor={
                      page === "error"
                        ? "var(--negative-bg)"
                        : "var(--neutral-bg)"
                    }
                    rows={[
                      {
                        rightTitle: val?.grade,
                        rightSubTitle: "Grade",
                        rightText: true,
                        leftText: true,
                        leftTitleTextColor: "var(--text-primary)",
                        leftTitle: val?.user_id,
                        leftSubTitle: "User ID",
                        rightTitleTextColor: "var(--text-primary)",
                        leftTextFontWeight: "normal",
                      },
                    ]}
                  />
                );
              })}
            </>
          </TabsWeapperSection>
        ) : /* ============================================================
           RENDER BRANCH 2: PARTIAL/SUCCESS WITH NO GRADE DATA
           Shows only approver info (selected user + previous approvers)
           when there are no predicted grades (partial) or no rule data (success).
           Condition: (partial && no predicted grades) || (success && no rule data)
        ============================================================ */
        (page === "partial" &&
            !predicted_approval?.predicted_approval_grades &&
            !predicted_approval?.predicted_approval_grades?.length >
              GlobalConfiguration?.lengthCheckZero) ||
          (page === "success" &&
            !rulePassedCount &&
            !rulePassedCount?.length > GlobalConfiguration?.lengthCheckZero) ? (
          <TabsWeapperSection title="Approved by ">
            <>
              <InfoCard
                bgColor={
                  page === "success"
                    ? "var(--positive-card)"
                    : page === "error"
                      ? "var(--negative-card)"
                      : "var(--neutral-card)"
                }
                header={true}
                rightHeaderText="Selected user"
                rightHeaderFontWeight="normal"
                rightHeaderTextSize="var(--font-xs)"
                rightHeaderTextColor="var(--text-secondary)"
                leftHeaderText={getFullName(selectedUser)}
                leftHeaderTextColor="var(--text-secondary)"
                leftHeaderTextSize="var(--font-xs)"
                borderColor={
                  page === "success"
                    ? "var(--positive-bg)"
                    : page === "error"
                      ? "var(--negative-bg)"
                      : "var(--neutral-bg)"
                }
                rows={[
                  {
                    rightTitle: selectedUser?.grade,
                    rightSubTitle: "Grade",
                    rightText: true,
                    rightTitleTextColor: "var(--text-primary)",
                    leftText: true,
                    leftTitle: selectedUser?.user_id,
                    leftSubTitle: "User ID",
                    leftTitleTextColor: "var(--text-primary)",
                    leftTextFontWeight: "normal",
                  },
                ]}
              />

              {selectedPreviousUsers?.map((val, index) => (
                <InfoCard
                  bgColor={
                    page === "success"
                      ? "var(--positive-card)"
                      : page === "error"
                        ? "var(--negative-card)"
                        : "var(--neutral-card)"
                  }
                  header={true}
                  rightHeaderText="Previous approver"
                  rightHeaderFontWeight="normal"
                  rightHeaderTextSize="var(--font-xs)"
                  rightHeaderTextColor="var(--text-secondary)"
                  leftHeaderText={getFullName(val)}
                  leftHeaderTextColor="var(--text-secondary)"
                  leftHeaderTextSize="var(--font-xs)"
                  borderColor={
                    page === "success"
                      ? "var(--positive-bg)"
                      : page === "error"
                        ? "var(--negative-bg)"
                        : "var(--neutral-bg)"
                  }
                  rows={[
                    {
                      rightTitle: val?.grade,
                      rightSubTitle: "Grade",
                      rightText: true,
                      rightTitleTextColor: "var(--text-primary)",
                      leftText: true,
                      leftTitle: val?.user_id,
                      leftSubTitle: "User ID",
                      leftTitleTextColor: "var(--text-primary)",
                      leftTextFontWeight: "normal",
                    },
                  ]}
                />
              ))}
            </>
          </TabsWeapperSection>
        ) : (
          /* ============================================================
             RENDER BRANCH 3: SUCCESS/PARTIAL WITH GRADE DATA
             Shows two tab sections:
             1. "Approved by" - Selected user + previous approvers (conditionally shown)
             2. "Passed By" / "Pending Approval/s" - Rule details with dynamic accounts
          ============================================================ */
          <>
            <TabsWeapperSection title="Approved by">
              {/*
                 Conditionally render approver info:
                 - Always show for error state
                 - Show for success/partial when selectedTab === "approve"
              */}
                <>
                  <InfoCard
                    bgColor={
                      page === "success"
                        ? "var(--positive-card)"
                        : page === "error"
                          ? "var(--negative-card)"
                          : "var(--neutral-card)"
                    }
                    header={true}
                    rightHeaderText="Selected user"
                    rightHeaderFontWeight="normal"
                    rightHeaderTextSize="var(--font-xs)"
                    rightHeaderTextColor="var(--text-secondary)"
                    leftHeaderText={getFullName(selectedUser)}
                    leftHeaderTextColor="var(--text-secondary)"
                    leftHeaderTextSize="var(--font-xs)"
                    borderColor={
                      page === "success"
                        ? "var(--positive-bg)"
                        : page === "error"
                          ? "var(--negative-bg)"
                          : "var(--neutral-bg)"
                    }
                    rows={[
                      {
                        rightTitle: selectedUser?.grade,
                        rightSubTitle: "Grade",
                        rightText: true,
                        rightTitleTextColor: "var(--text-primary)",
                        leftText: true,
                        leftTitle: selectedUser?.user_id,
                        leftSubTitle: "User ID",
                        leftTitleTextColor: "var(--text-primary)",
                        leftTextFontWeight: "normal",
                      },
                    ]}
                  />

                  {selectedPreviousUsers?.map((val, index) => (
                    <InfoCard
                      bgColor={
                        page === "success"
                          ? "var(--positive-card)"
                          : page === "error"
                            ? "var(--negative-card)"
                            : "var(--neutral-card)"
                      }
                      header={true}
                      rightHeaderText="Previous approver"
                      rightHeaderFontWeight="normal"
                      rightHeaderTextSize="var(--font-xs)"
                      rightHeaderTextColor="var(--text-secondary)"
                      leftHeaderText={getFullName(val)}
                      leftHeaderTextColor="var(--text-secondary)"
                      leftHeaderTextSize="var(--font-xs)"
                      borderColor={
                        page === "success"
                          ? "var(--positive-bg)"
                          : page === "error"
                            ? "var(--negative-bg)"
                            : "var(--neutral-bg)"
                      }
                      rows={[
                        {
                          rightTitle: val?.grade,
                          rightSubTitle: "Grade",
                          rightText: true,
                          rightTitleTextColor: "var(--text-primary)",
                          leftText: true,
                          leftTitle: val?.user_id,
                          leftSubTitle: "User ID",
                          leftTitleTextColor: "var(--text-primary)",
                          leftTextFontWeight: "normal",
                        },
                      ]}
                    />
                  ))}
                </>
            </TabsWeapperSection>

            {/*
               Second tab section: Shows rule details for success,
               or pending approval grades for partial state
            */}
            <TabsWeapperSection
              title={page === "success" ? "Passed By" : "Pending Approval/s"}
            >
              <>
                <div
                  // Disable ticker animation for non-success states
                  className={
                    page === "success"
                      ? ""
                      : "[&_.whitespace-nowrap]:whitespace-normal [&_.ticker-text]:block [&_.ticker-text]:whitespace-normal [&_.ticker-text]:!animate-none [&_.ticker-text]:[transform:none]"
                  }
                >
                  <InfoCard
                    bgColor={
                      page === "success"
                        ? "var(--positive-card)"
                        : "var(--neutral-card)"
                    }
                    header={true}
                    // Left header: "Rule" for success, descriptive text for partial
                    leftHeaderText={
                      page === "success"
                        ? "Rule"
                        : "Approvals are pending from individual or all of the following grades."
                    }
                    leftHeaderFontWeight="normal"
                    // Right header: Amount range for success, empty for partial
                    rightHeaderText={
                      page === "success" ? (
                        <>
                          ₹{" "}
                          <FormatAmount
                            price={
                              transaction_auth_matrices?.[
                                GlobalConfiguration?.lengthCheckZero
                              ]?.lower_amount
                            }
                          />{" "}
                          🡢 ₹{" "}
                          <FormatAmount
                            price={
                              transaction_auth_matrices?.[
                                GlobalConfiguration?.lengthCheckZero
                              ]?.higher_amount
                            }
                          />
                        </>
                      ) : (
                        ""
                      )
                    }
                    rightHeaderTextColor="var(--text-secondary)"
                    rightHeaderTextSize="var(--font-xs)"
                    borderColor={
                      page === "success"
                        ? "var(--positive-bg)"
                        : "var(--neutral-bg)"
                    }
                    // dynamicAccounts computed earlier based on page state
                    rows={dynamicAccounts}
                  />
                </div>
              </>
            </TabsWeapperSection>
          </>
        )}
      </StatusContainer>
    </>
  );
};

export default TestAuthMatrixOutcomePage;
